import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface HolidayGroup {
  id: string;
  code: string;
  name: string;
  holiday_calendar_id: string;
}
interface Calendar {
  id: string;
  code: string;
  name: string;
  calendar_year: number;
}
interface Employee {
  id: string;
  code: string;
  first_name: string;
  last_name: string;
}

const EMPTY = { code: '', name: '', holidayCalendarId: '' };

export default function HolidayGroups() {
  const [groups, setGroups] = useState<HolidayGroup[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<HolidayGroup | null>(null);
  const [error, setError] = useState('');

  const [assignTarget, setAssignTarget] = useState<HolidayGroup | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [assignedEmployees, setAssignedEmployees] = useState<Employee[]>([]);
  const [pickEmployeeIds, setPickEmployeeIds] = useState<string[]>([]);

  const load = async () => {
    const [g, c] = await Promise.all([api.get('/payroll/masters/holiday-groups'), api.get('/lms/holiday-calendars')]);
    setGroups(g.data.holidayGroups || []);
    setCalendars(c.data.calendars || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: HolidayGroup) => {
    setEditingId(row.id);
    setForm({ code: row.code, name: row.name, holidayCalendarId: row.holiday_calendar_id || '' });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`/payroll/masters/holiday-groups/${editingId}`, form);
      } else {
        await api.post('/payroll/masters/holiday-groups', form);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/payroll/masters/holiday-groups/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const openAssign = async (row: HolidayGroup) => {
    setAssignTarget(row);
    setPickEmployeeIds([]);
    const [empRes, assignedRes] = await Promise.all([api.get('/employees'), api.get(`/payroll/masters/holiday-groups/${row.id}/employees`)]);
    setAllEmployees(empRes.data.employees || []);
    setAssignedEmployees(assignedRes.data.employees || []);
  };

  const saveAssign = async () => {
    if (!assignTarget || !pickEmployeeIds.length) return;
    await api.post(`/payroll/masters/holiday-groups/${assignTarget.id}/assign`, { employeeIds: pickEmployeeIds });
    const assignedRes = await api.get(`/payroll/masters/holiday-groups/${assignTarget.id}/employees`);
    setAssignedEmployees(assignedRes.data.employees || []);
    setPickEmployeeIds([]);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Holiday Group</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" onClick={() => openAssign(row)}>
                  Assign Employees
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={groups}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Holiday Group' : 'New Holiday Group'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select
            label="Holiday Calendar"
            options={calendars.map((c) => ({ value: c.id, label: `${c.name} (${c.calendar_year})` }))}
            value={form.holidayCalendarId}
            onChange={(e) => setForm({ ...form, holidayCalendarId: e.target.value })}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!assignTarget} onClose={() => setAssignTarget(null)} title={`Assign Employees — ${assignTarget?.name}`}>
        <div className="flex flex-col gap-4">
          <select
            multiple
            className="border rounded-lg p-2 h-40"
            value={pickEmployeeIds}
            onChange={(e) => setPickEmployeeIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
          >
            {allEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.code} — {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
          <Button onClick={saveAssign}>Assign Selected</Button>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Currently Assigned</h4>
            <ul className="text-sm text-gray-600 list-disc pl-4">
              {assignedEmployees.map((e) => (
                <li key={e.id}>
                  {e.code} — {e.first_name} {e.last_name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete holiday group "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
